import React, { useCallback, useContext, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import { FiSave } from 'react-icons/fi';
import { DateObject } from 'react-multi-date-picker';
import { ThemeContext } from 'styled-components';
import { addHours, format } from 'date-fns';
import { Dictionary } from '@fullcalendar/react';

import { OrientacaoExibicao as IOrientacaoExibicao } from 'pages/@types';
import api from 'services/api';
import {
  FormatoOrientacaoExib,
  FormatosOrientacaoExibOptions,
} from 'utils/variables';
import { Form, FormContent, FormGroup } from './styles';
import getValidationErrors from 'services/getValidationErros';
import AppError from 'utils/AppError';
import { EmissoraOption, SelectOption } from '.';

import { Event } from 'components/Calendar';
import Button from 'components/Button';
import Checkbox from 'components/Checkbox';
import Select from 'components/Select';
import Input from 'components/Input';
import TextArea from 'components/TextArea';
import Spinner from 'components/Spinner';
import Tooltip from 'components/Tooltip';
import CalendarDatePicker from 'components/CalendarDatePicker';

// configuração do validator
const schema = Yup.object().shape({
  emissoras: Yup.string().required('Campo Obrigatório'),
  formato: Yup.string().required('Campo Obrigatório'),
  programa: Yup.string().when('formato', {
    is: formato => formato && formato !== FormatoOrientacaoExib.OUTROS,
    then: Yup.string().required('Campo Obrigatório'),
  }),
  titulo: Yup.string().when('formato', {
    is: formato => formato && formato !== FormatoOrientacaoExib.OUTROS,
    then: Yup.string().required('Campo Obrigatório'),
  }),
  cliente: Yup.string().when('formato', {
    is: formato => formato && formato !== FormatoOrientacaoExib.OUTROS,
    then: Yup.string().required('Campo Obrigatório'),
  }),
  duracao: Yup.string().when('formato', {
    is: formato =>
      formato &&
      (formato.includes(FormatoOrientacaoExib.BOLETIM) ||
        formato.includes(FormatoOrientacaoExib.BREAKEXCLUSIVO) ||
        formato.includes(FormatoOrientacaoExib.INSERCOESCOMBINADAS)),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  break: Yup.string()
    .max(20)
    .when('formato', {
      is: formato =>
        formato &&
        (formato.includes(FormatoOrientacaoExib.BREAKDETERMINADO) ||
          formato.includes(FormatoOrientacaoExib.ORA)),
      then: Yup.string().required('Campo Obrigatório'),
    }),
  titulo2: Yup.string().when('formato', {
    is: formato =>
      formato && formato.includes(FormatoOrientacaoExib.INSERCOESCOMBINADAS),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  cliente2: Yup.string().when('formato', {
    is: formato =>
      formato && formato.includes(FormatoOrientacaoExib.INSERCOESCOMBINADAS),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  duracao2: Yup.string().when('formato', {
    is: formato =>
      formato && formato.includes(FormatoOrientacaoExib.INSERCOESCOMBINADAS),
    then: Yup.string().required('Campo Obrigatório'),
  }),
  codmaterial: Yup.number()
    .typeError('Cod. material inválido')
    .transform((v, o) => (o === '' ? undefined : v))
    .notRequired()
    .min(10000, 'Cod. material inválido')
    .max(999999, 'Cod. material inválido'),
});

const schemaNew = schema.shape({
  datas: Yup.array().required('Campo Obrigatório'),
});
interface IProps {
  clientes: SelectOption[];
  programas: SelectOption[];
  emissora: SelectOption;
  emissoras: EmissoraOption[];
  loadingFormOptions: boolean;
  editMode: boolean;
  initialData: any;
  setEvents: React.Dispatch<React.SetStateAction<Event[]>>;
  setSelected: React.Dispatch<React.SetStateAction<Dictionary | undefined>>;
  setInitialData: React.Dispatch<React.SetStateAction<any>>;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function OrientacaoExibicaoStore(props: IProps) {
  const [formatos, setFormatos] = useState<string[]>(
    props.initialData.formato || [],
  );
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [disableComercial, setDisableComercial] = useState(false);
  const [disableComercial2, setDisableComercial2] = useState(false);
  const [rede, setRede] = useState(true);

  // USE REF
  const formRef = useRef<FormHandles>(null);

  // USE CONTEXT
  const themeContext = useContext(ThemeContext);

  const handleRede = useCallback(e => {
    e.preventDefault();
    if (formRef.current) {
      const emissoras = formRef.current.getFieldValue('emissoras');
      if (emissoras.length === 8) {
        formRef.current.clearField('emissoras');
        setRede(false);
      } else {
        const rede = [1, 2, 3, 4, 5, 6, 7, 8].join();
        formRef.current.setFieldValue('emissoras', rede);
        setRede(true);
      }
    }
  }, []);

  const handleSubmit = useCallback(
    async data => {
      try {
        if (formRef.current) {
          setLoadingSubmit(true);
          formRef.current.setErrors({});
          // Deletando fields vazios
          Object.keys(data).forEach(key => {
            if (!data[key]) delete data[key];
          });

          if (props.editMode) {
            const selectedId = props.initialData.id;
            await schema.validate(data, { abortEarly: false });

            // Removendo emissoras do payload
            delete data.emissoras;
            const response = await api.patch(
              `/orientacaoexibicao/${selectedId}`,
              {
                ...data,
                formato: data.formato.join(),
              },
            );

            if (response?.data) {
              const editedEvent = {
                ...response.data,
                programa: response.data.programa.id || response.data.programa,
                title: response.data.formato.replace(',', '+'),
                start: response.data.data,
                end: response.data.data,
                allDay: true,
                ...(response.data.formato.includes(',') && {
                  backgroundColor: themeContext?.calendar.multiploformato,
                }),
                borderColor: 'transparent',
              };
              props.setEvents(prevState =>
                prevState.map(p => (p.id == selectedId ? editedEvent : p)),
              );
              formRef.current.reset();
              props.setSelected({});
              props.setInitialData({});
              props.setEditMode(false);
              toast.success('Observação atualizada');
            } else toast.error('Falha no cadastro das observações');
          } else {
            await schemaNew.validate(data, { abortEarly: false });

            const response = await api.post('/orientacaoexibicao', {
              ...data,
              emissoras: data.emissoras.map(Number),
              formato: data.formato.join(),
              datas: data.datas.map(
                (value: DateObject) =>
                  `${value.year}-${value.month}-${value.day}`,
              ),
            });

            if (response?.data) {
              const newEvents = response.data
                .filter(
                  (o: IOrientacaoExibicao) =>
                    o.emissora.id === props.emissora.value,
                )
                .map((o: IOrientacaoExibicao) => ({
                  ...o,
                  title: o.formato.replace(',', '+'),
                  start: addHours(new Date(o.data), 3),
                  end: addHours(new Date(o.data), 3),
                  allDay: true,
                  ...(o.formato.includes(',') && {
                    backgroundColor: themeContext?.calendar.multiploformato,
                  }),
                  borderColor: 'transparent',
                }));

              toast.success('Observações cadastradas');
              // Adicionar novo(s) evento(s) ao calendário
              props.setEvents(prevState => [...prevState, ...newEvents]);

              // Limpando formulário para próximo cadastro
              formRef.current.reset();
            } else toast.error('Falha no cadastro das observações');
          }
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          if (formRef.current) formRef.current.setErrors(errors);
          toast.error(err.message);
        } else if (err instanceof Error) {
          toast.error(err.message);
        } else {
          const appError = err as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingSubmit(false);
      }
    },
    [props, themeContext?.calendar.multiploformato],
  );

  const handleLimparComercial = useCallback((e, isComercial2) => {
    e.preventDefault();
    if (!isComercial2) {
      formRef.current?.clearField('codmaterial');
      formRef.current?.clearField('titulo');
      formRef.current?.clearField('duracao');
      formRef.current?.clearField('cliente');
      setDisableComercial(false);
    } else {
      formRef.current?.clearField('codmaterial2');
      formRef.current?.clearField('titulo2');
      formRef.current?.clearField('duracao2');
      formRef.current?.clearField('cliente2');
      setDisableComercial2(false);
    }
  }, []);

  const loadComercial = useCallback(async (e, isComercial2: boolean) => {
    e.preventDefault();
    const codmaterial = e.target.value;
    if (!/^\d{5,6}$/.test(codmaterial)) {
      toast.error('Cod. material inválido');
      return;
    }
    const response = await api.get('/comerciaists/codmaterial', {
      params: { codmaterial },
    });
    if (!response) throw new AppError('Falha de comunicação com o servidor');
    if (response?.data) {
      const ultimoComercial = response.data;
      if (!isComercial2) {
        formRef.current?.setFieldValue('titulo', ultimoComercial.titulo);
        formRef.current?.setFieldValue('cliente', {
          value: ultimoComercial.cliente.id,
          label: ultimoComercial.cliente.mnemonico,
        });
        formRef.current?.setFieldValue('duracao', ultimoComercial.duracao);
        setDisableComercial(true);
      } else {
        formRef.current?.setFieldValue('titulo2', ultimoComercial.titulo);
        formRef.current?.setFieldValue('cliente2', {
          value: ultimoComercial.cliente.id,
          label: ultimoComercial.cliente.mnemonico,
        });
        formRef.current?.setFieldValue('duracao2', ultimoComercial.duracao);
        setDisableComercial2(true);
      }
    } else {
      toast.error(`Nenhum comercial encontrado com o CM ${codmaterial}`);
    }
  }, []);

  return (
    <FormContent>
      {props.initialData && props.editMode && (
        <header>
          <h3>ORIENTAÇÃO {props.initialData.id}</h3>
          <h3>
            DATA:{' '}
            {format(
              addHours(new Date(props.initialData.data), 3),
              'dd/MM/yyyy',
            )}
          </h3>
        </header>
      )}
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        initialData={props.initialData}
      >
        <div>
          <section>
            <h5>Exibidoras: </h5>
            <Button
              fill
              type="button"
              color="primary"
              size="xsm"
              onClick={e => handleRede(e)}
              disabled={props.editMode}
            >
              REDE ? {rede ? 'SIM' : 'NÃO'}
            </Button>
          </section>
          <Checkbox
            name="emissoras"
            columns="1fr 1fr 1fr"
            options={props.emissoras}
            disabled={props.loadingFormOptions || props.editMode}
            handleSelected={() =>
              setRede(formRef.current?.getFieldValue('emissora').length === 8)
            }
          />
          <section>
            <h5>Formato: </h5>
          </section>
          <Checkbox
            name="formato"
            columns="1fr 1fr 1fr"
            options={FormatosOrientacaoExibOptions}
            disabled={props.loadingFormOptions}
            handleSelected={value => {
              if (value.checked)
                setFormatos(prevState => [...prevState, value.id]);
              else
                setFormatos(prevState => prevState.filter(p => p !== value.id));
            }}
          />
          <section>
            <h5>Programa: </h5>
          </section>
          <FormGroup isPrograma={true}>
            <Select
              name="programa"
              placeholder="Selecione o programa"
              disabled={props.loadingFormOptions}
              options={props.programas}
              isClearable
            />
            <Select
              name="break"
              placeholder="Selecione o Intervalo"
              options={[
                { value: 'Intervalo 1', label: 'Intervalo 1' },
                { value: 'Intervalo 2', label: 'Intervalo 2' },
                { value: 'Intervalo 3', label: 'Intervalo 3' },
                { value: 'Intervalo 4', label: 'Intervalo 4' },
                { value: 'Intervalo 5', label: 'Intervalo 5' },
                { value: 'Último intervalo', label: 'Último intervalo' },
                { value: 'Qualquer intervalo', label: 'Qualquer intervalo' },
              ]}
              isClearable
            />
          </FormGroup>
          <div>
            <section>
              <h5>Comercial 1: </h5>
              <Button
                type="button"
                fill
                color="primary"
                size="xsm"
                disabled={!disableComercial}
                onClick={e => handleLimparComercial(e, false)}
              >
                LIMPAR
              </Button>
            </section>
            <FormGroup>
              <Input
                id="codmaterial"
                name="codmaterial"
                placeholder="Cod. material"
                max={6}
                disabled={disableComercial}
                onKeyDown={e => {
                  if (e.key === 'Enter') loadComercial(e, false);
                }}
              />
              <Input
                name="titulo"
                placeholder="Título"
                disabled={disableComercial}
              />
            </FormGroup>
            <FormGroup>
              <Input
                name="duracao"
                placeholder="Duração"
                disabled={disableComercial}
              />

              <Select
                name="cliente"
                placeholder="Selecione o cliente"
                disabled={props.loadingFormOptions || disableComercial}
                options={props.clientes}
                isClearable
              />
            </FormGroup>
          </div>
          {(formatos.includes(FormatoOrientacaoExib.BOLETIM) ||
            formatos.includes(FormatoOrientacaoExib.INSERCOESCOMBINADAS)) && (
            <div>
              <section>
                <h5>Comercial 2: </h5>
                <Button
                  type="button"
                  fill
                  color="primary"
                  size="xsm"
                  disabled={!disableComercial2}
                  onClick={e => {
                    handleLimparComercial(e, true);
                  }}
                >
                  LIMPAR
                </Button>
              </section>
              <FormGroup>
                <Input
                  id="codmaterial"
                  name="codmaterial2"
                  placeholder="Cod. material"
                  max={6}
                  disabled={disableComercial2}
                  onKeyDown={e => {
                    if (e.key === 'Enter') loadComercial(e, true);
                  }}
                />
                <Input
                  name="titulo2"
                  placeholder="Título"
                  disabled={disableComercial2}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  name="duracao2"
                  placeholder="Duração"
                  disabled={disableComercial2}
                />

                <Select
                  name="cliente2"
                  placeholder="Selecione o cliente"
                  disabled={props.loadingFormOptions || disableComercial2}
                  options={props.clientes}
                  isClearable
                />
              </FormGroup>
            </div>
          )}
          <Tooltip target="codmaterial" position="left">
            Digite o CM e pressione enter para buscar os dados
          </Tooltip>
          <section>
            <h5>Observações: </h5>
          </section>
          <TextArea name="observacao" height="55" />

          {!props.editMode && (
            <>
              <section>
                <h5>Datas: </h5>
              </section>
              <CalendarDatePicker name="datas" />
            </>
          )}
        </div>
        <Button type="submit" color="primary" disabled={loadingSubmit}>
          <FiSave />
          {!loadingSubmit ? 'SALVAR' : <Spinner type="ThreeDots" />}
        </Button>
      </Form>
    </FormContent>
  );
}
